import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiShoppingBag } from 'react-icons/fi';
import { TbShieldCheck } from 'react-icons/tb';

const SampleRequestConfirmationModal = ({
  open,
  onClose,
  onProceedSample,
  onSwitchToFullOrder,
  cartItems = [],
}) => {
  const [animate, setAnimate] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (loading) return;
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleProceedSample = async () => {
    setLoading(true);
    try {
      await onProceedSample();
      // onProceedSample handles its own toast + state updates;
      // modal will close naturally once parent refreshes
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleFullOrder = () => {
    if (loading) return;
    if (window.confirm('This will clear your sample cart. Are you sure you want to proceed with a full order instead?')) {
      handleClose();
      onSwitchToFullOrder();
    }
  };

  const totalAmount = cartItems.reduce(
    (sum, item) => sum + (item.priceBreakDown?.totalAmount || 0),
    0
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${animate ? 'opacity-100' : 'opacity-0'
        }`}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full md:w-[480px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${animate
          ? 'scale-100 translate-y-0'
          : 'scale-95 md:translate-y-4 translate-y-full'
          }`}
        style={{ maxHeight: '90vh' }}
      >
        {/* ── Header ── */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center gap-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-2xl disabled:opacity-50 leading-none"
          >
            ←
          </button>
          <div className="flex-1 font-semibold text-lg flex items-center gap-2">
            <FiShoppingBag />
            Confirm Sample Request
          </div>
        </div>

        {/* ── Body ── */}
        <div className="p-5 overflow-auto flex-1 space-y-4">

          {/* Cart summary */}
          <div className="rounded-2xl border border-primaryColor/20 bg-primaryColor/5 p-4">
            <p className="text-sm font-bold text-gray-900 mb-1">
              {cartItems.length} quotation{cartItems.length !== 1 ? 's' : ''} in your sample cart
            </p>
            <p className="text-xs text-gray-600 leading-5">
              Estimated total:{' '}
              <span className="font-bold text-primaryColor">
                ₹{Math.round(totalAmount).toLocaleString('en-IN')}
              </span>{' '}
              <span className="text-gray-400">(transport charges not yet added)</span>
            </p>
          </div>

          {/* What happens next */}
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <TbShieldCheck className="text-blue-600 flex-shrink-0" size={16} />
              <p className="text-sm font-bold text-blue-700">What happens after you confirm?</p>
            </div>
            <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside leading-5">
              <li>Your sample request will be submitted to PCM Exports.</li>
              <li>Admin will review and finalize the transportation charges for each quotation.</li>
              <li>Once charges are confirmed, you can proceed to payment for each sample.</li>
              <li>Samples will be dispatched after payment is received.</li>
            </ol>
          </div>

          {/* Tip: full order alternative */}
          <div className="rounded-xl border-l-4 border-amber-400 bg-amber-50 p-3">
            <p className="text-xs text-gray-700 leading-5">
              <span className="font-bold text-amber-700">💡 Tip:</span>{' '}
              If you'd prefer to skip samples and place a full order directly, use the
              <span className="font-semibold"> "Proceed with Full Order"</span> button below.
              This will clear your sample cart and let you select a single quotation for full
              order confirmation.
            </p>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={handleFullOrder}
            disabled={loading}
            className="flex-1 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
          >
            Full Order Instead
          </button>
          <button
            onClick={handleProceedSample}
            disabled={loading}
            className="flex-[2] py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-xl font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-primaryColor/20"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              'Confirm Sample Request'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SampleRequestConfirmationModal;